import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { tasksApi, knowledgeBasesApi } from '../utils/api';
import type { Task } from '../types/task';
import { TaskStatus } from '../types/task';
import { useWebSocket } from '../utils/WebSocketContext';
import FileUploader from '../components/FileUploader';
import NavigationTree from '../components/NavigationTree';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { parseMarkdownHeaders, buildNavigationTree } from '../utils/markdownNav';
import type { NavigationNode } from '../utils/markdownNav';
import type { Components } from 'react-markdown';

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.DRAFT:
      return 'bg-yellow-100 text-yellow-700';
    case TaskStatus.PENDING:
      return 'bg-gray-100 text-gray-700';
    case TaskStatus.PROCESSING:
      return 'bg-blue-100 text-blue-700';
    case TaskStatus.COMPLETED:
      return 'bg-green-100 text-green-700';
    case TaskStatus.FAILED:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Types for navigation structure
interface IterationNav {
  iterationNumber: number;
  iterationId: number;
  tree: NavigationNode[];
  hasOutputFiles: boolean;
}

// Generate unique ID for a header within an iteration
const generateHeaderId = (iterationId: number, headerId: string): string => {
  return `iteration-${iterationId}-${headerId}`;
};

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lastUpdate } = useWebSocket();
  const [task, setTask] = useState<Task | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [refinementFiles, setRefinementFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convert to KB state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [kbTitle, setKbTitle] = useState('');
  const [kbDescription, setKbDescription] = useState('');
  const [includeInputFiles, setIncludeInputFiles] = useState(true);
  const [includeOutputFiles, setIncludeOutputFiles] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false);

  // Delete/Archive state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Navigation sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Iteration collapse state - track which iterations are expanded
  const [expandedIterations, setExpandedIterations] = useState<Set<number>>(new Set());
  // Agent Report collapse state - track which iterations have their agent reports expanded
  const [expandedAgentReports, setExpandedAgentReports] = useState<Set<number>>(new Set());
  const initialScrollDone = useRef(false);

  // Build navigation structure from task iterations
  const navigationStructure = useMemo<IterationNav[]>(() => {
    if (!task) return [];

    return task.iterations.map((iteration) => {
      const headers = iteration.agent_report ? parseMarkdownHeaders(iteration.agent_report) : [];
      const tree = buildNavigationTree(headers);
      return {
        iterationNumber: iteration.iteration_number,
        iterationId: iteration.id,
        tree,
        hasOutputFiles: iteration.output_files.length > 0,
      };
    });
  }, [task]);

  // Collect element IDs for scroll spy - only from EXPANDED iterations
  // This ensures we only observe elements that actually exist in the DOM
  const allElementIds = useMemo(() => {
    const ids: string[] = [];
    navigationStructure.forEach((iterNav) => {
      // Always add iteration header ID (always visible)
      ids.push(`iteration-${iterNav.iterationId}`);

      // Only add header IDs if this iteration is expanded
      if (expandedIterations.has(iterNav.iterationId)) {
        // Add all header IDs (flattened from tree)
        const collectIds = (node: NavigationNode) => {
          ids.push(generateHeaderId(iterNav.iterationId, node.id));
          node.children.forEach(collectIds);
        };
        iterNav.tree.forEach((node) => collectIds(node));
        // Add output files section if present
        if (iterNav.hasOutputFiles) {
          ids.push(`iteration-${iterNav.iterationId}-output-files`);
        }
      }
    });
    // Add refinement section
    if (task?.status !== TaskStatus.PROCESSING) {
      ids.push('submit-refinement');
    }
    return ids;
  }, [navigationStructure, task?.status, expandedIterations]);

  // Scroll spy to track active section
  const activeElementId = useScrollSpy(allElementIds);

  // Sidebar ref for auto-scrolling
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Navigate to element - expands iteration/agent report if needed, then scrolls
  const navigateToElement = useCallback((elementId: string) => {
    // Parse the element ID to determine iteration and element type
    // Patterns: "iteration-{id}", "iteration-{id}-output-files", "iteration-{id}-{headerId}"
    const iterationMatch = elementId.match(/^iteration-(\d+)(?:-(.+))?$/);

    if (iterationMatch) {
      const iterationId = parseInt(iterationMatch[1]);
      const suffix = iterationMatch[2]; // undefined for iteration header, "output-files" or header id

      let needsExpand = false;
      let needsAgentReportExpand = false;

      // Check if iteration needs to be expanded
      if (!expandedIterations.has(iterationId)) {
        setExpandedIterations(prev => new Set([...prev, iterationId]));
        needsExpand = true;
      }

      // If navigating to a markdown header (not iteration header, not output-files), expand agent report
      if (suffix && suffix !== 'output-files') {
        if (!expandedAgentReports.has(iterationId)) {
          setExpandedAgentReports(prev => new Set([...prev, iterationId]));
          needsAgentReportExpand = true;
        }
      }

      // If we expanded something, wait for DOM to update before scrolling
      if (needsExpand || needsAgentReportExpand) {
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        return;
      }
    }

    // No expansion needed, scroll immediately
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandedIterations, expandedAgentReports]);

  // Toggle iteration expansion
  const toggleIterationExpand = useCallback((iterationId: number) => {
    setExpandedIterations(prev => {
      const next = new Set(prev);
      if (next.has(iterationId)) {
        next.delete(iterationId);
      } else {
        next.add(iterationId);
      }
      return next;
    });
  }, []);

  // Toggle agent report expansion within an iteration
  const toggleAgentReportExpand = useCallback((iterationId: number) => {
    setExpandedAgentReports(prev => {
      const next = new Set(prev);
      if (next.has(iterationId)) {
        next.delete(iterationId);
      } else {
        next.add(iterationId);
      }
      return next;
    });
  }, []);

  // Initialize expanded iterations when task loads - only expand latest
  useEffect(() => {
    if (task && task.iterations.length > 0) {
      const latestIteration = task.iterations[task.iterations.length - 1];
      setExpandedIterations(new Set([latestIteration.id]));
      // Also expand the latest iteration's agent report by default
      setExpandedAgentReports(new Set([latestIteration.id]));
    }
  }, [task?.id]); // Only run when task ID changes

  // Auto-scroll to last iteration on initial load
  useEffect(() => {
    if (task && task.iterations.length > 0 && !initialScrollDone.current) {
      initialScrollDone.current = true;
      const lastIteration = task.iterations[task.iterations.length - 1];
      // Slight delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(`iteration-${lastIteration.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [task]);

  // Auto-scroll sidebar to show active element
  useEffect(() => {
    if (activeElementId && sidebarRef.current) {
      const activeNavItem = sidebarRef.current.querySelector(`[data-nav-id="${activeElementId}"]`);
      if (activeNavItem) {
        const sidebar = sidebarRef.current;
        const itemRect = activeNavItem.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();

        // Check if item is out of view
        if (itemRect.top < sidebarRect.top || itemRect.bottom > sidebarRect.bottom) {
          activeNavItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [activeElementId]);

  // Debug: log activeElementId changes
  useEffect(() => {
    console.log('[TaskDetail] activeElementId changed to:', activeElementId);
    if (navigationStructure.length > 0) {
      const firstIterationId = `iteration-${navigationStructure[0].iterationId}`;
      console.log('[TaskDetail] First iteration would be:', firstIterationId, '- matches:', activeElementId === firstIterationId);
    }
  }, [activeElementId, navigationStructure]);

  const loadTask = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const data = await tasksApi.getById(parseInt(id));

      // If it's a draft task, redirect to edit wizard
      if (data.status === TaskStatus.DRAFT) {
        navigate(`/tasks/${id}/edit`);
        return;
      }

      setTask(data);
      setError('');

      // Mark as read if it's unread
      if (data.unread) {
        try {
          await tasksApi.markAsRead(parseInt(id));
          // Update local state to reflect read status
          setTask(prevTask => prevTask ? { ...prevTask, unread: false } : null);
        } catch (err) {
          console.error('Failed to mark task as read:', err);
        }
      }
    } catch (err) {
      setError('Failed to load task');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
  }, [id]);

  // Listen for WebSocket task updates
  useEffect(() => {
    if (lastUpdate && task && lastUpdate.id === task.id) {
      // Update task status from WebSocket
      setTask(prev => prev ? {
        ...prev,
        status: lastUpdate.status as TaskStatus,
        updated_at: lastUpdate.updated_at
      } : null);

      // Reload full task to get latest iterations/files if status changed
      if (lastUpdate.status !== task.status) {
        loadTask();
      }
    }
  }, [lastUpdate, task?.id]);

  const handleAddIteration = async () => {
    if (!id || !newPrompt.trim()) return;
    try {
      setIsSubmitting(true);

      // Step 1: Create iteration with JSON (creates in DRAFT status)
      const iteration = await tasksApi.createIteration(parseInt(id), { user_prompt: newPrompt });

      // Step 2: Upload files if any
      if (refinementFiles.length > 0) {
        const formData = new FormData();
        refinementFiles.forEach((file) => {
          formData.append('files', file);
        });
        await tasksApi.uploadIterationFiles(parseInt(id), iteration.id, formData);
      }

      // Step 3: Submit iteration (sets task to PENDING for agent processing)
      await tasksApi.submitIteration(parseInt(id), iteration.id);

      setNewPrompt('');
      setRefinementFiles([]);
      await loadTask();
    } catch (err) {
      console.error('Failed to create iteration:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadFile = async (fileId: number, fileName: string) => {
    if (!id) return;
    try {
      const blob = await tasksApi.downloadFile(parseInt(id), fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  const handleDownloadIterationOutputFiles = async (iterationId: number, iterationNumber: number) => {
    if (!id) return;
    try {
      const blob = await tasksApi.downloadIterationOutputFiles(parseInt(id), iterationId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `task_${id}_iteration_${iterationNumber}_files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download iteration output files:', err);
    }
  };

  const handleConvertToKB = async () => {
    if (!id || !kbTitle.trim()) return;

    try {
      setIsConverting(true);
      const kb = await knowledgeBasesApi.convertFromTask(parseInt(id), {
        title: kbTitle.trim(),
        description: kbDescription.trim() || undefined,
        include_input_files: includeInputFiles,
        include_output_files: includeOutputFiles,
      });
      navigate(`/knowledge-bases/${kb.id}`);
    } catch (err) {
      console.error('Failed to convert to knowledge base:', err);
      alert('Failed to convert to knowledge base');
    } finally {
      setIsConverting(false);
    }
  };

  const handleSubmitDraft = async () => {
    if (!id) return;

    try {
      setIsSubmittingDraft(true);
      await tasksApi.submitDraft(parseInt(id));
      await loadTask(); // Reload to show updated status
    } catch (err) {
      console.error('Failed to submit draft:', err);
      alert('Failed to submit draft task');
    } finally {
      setIsSubmittingDraft(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!id) return;

    try {
      setIsDeleting(true);
      await tasksApi.delete(parseInt(id));
      navigate('/tasks'); // Redirect to task list after deletion
    } catch (err) {
      console.error('Failed to delete draft:', err);
      alert('Failed to delete draft task');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleArchiveTask = async () => {
    if (!id) return;

    try {
      setIsArchiving(true);
      await tasksApi.archive(parseInt(id));
      navigate('/tasks'); // Redirect to task list after archiving
    } catch (err) {
      console.error('Failed to archive task:', err);
      alert('Failed to archive task');
      setIsArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  if (isLoading && !task) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Task not found'}</p>
          <button
            onClick={() => navigate('/tasks')}
            className="mt-4 px-4 py-2 btn-secondary rounded-md text-sm"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{task.title} - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Sidebar Navigation */}
        {navigationStructure.length > 0 && (
          <div
            className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 shadow-lg transition-transform duration-300 z-40 ${
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{ width: '280px' }}
          >
            <div ref={sidebarRef} className="h-full overflow-y-auto p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-sm text-gray-700">Navigation</h3>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close sidebar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <nav className="space-y-3">
                {navigationStructure.map((iterNav, navIndex) => {
                  const iterationElementId = `iteration-${iterNav.iterationId}`;
                  const isIterationActive = activeElementId === iterationElementId;
                  const outputFilesId = `iteration-${iterNav.iterationId}-output-files`;
                  const isOutputFilesActive = activeElementId === outputFilesId;
                  const isExpanded = expandedIterations.has(iterNav.iterationId);
                  const isLatestIteration = navIndex === navigationStructure.length - 1;

                  return (
                    <div key={iterNav.iterationId} className="border-b border-gray-100 pb-3 last:border-0">
                      {/* Iteration header with expand/collapse */}
                      <div
                        data-nav-id={iterationElementId}
                        className={`flex items-center rounded transition-colors ${
                          isIterationActive
                            ? 'bg-blue-100 text-blue-800'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <button
                          onClick={() => toggleIterationExpand(iterNav.iterationId)}
                          className="p-1.5 text-gray-400 hover:text-gray-600"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigateToElement(iterationElementId)}
                          className="flex-1 text-left py-1.5 text-sm font-medium"
                        >
                          <span className="flex items-center">
                            {isIterationActive && (
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />
                            )}
                            Iteration {iterNav.iterationNumber}
                            {isLatestIteration && (
                              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded">Latest</span>
                            )}
                          </span>
                        </button>
                      </div>

                      {/* Collapsible content */}
                      {isExpanded && (
                        <>
                          {/* Header tree using NavigationTree component */}
                          {iterNav.tree.length > 0 && (
                            <div className="mt-2 ml-2">
                              <NavigationTree
                                nodes={iterNav.tree}
                                activeId={activeElementId}
                                onNavigate={navigateToElement}
                                idPrefix={`iteration-${iterNav.iterationId}`}
                                showExpandAll={iterNav.tree.length > 3}
                              />
                            </div>
                          )}

                          {/* Output files link */}
                          {iterNav.hasOutputFiles && (
                            <div className="mt-2 ml-2" data-nav-id={outputFilesId}>
                              <button
                                onClick={() => navigateToElement(outputFilesId)}
                                className={`w-full text-left px-2 py-1 text-xs rounded transition-colors flex items-center ${
                                  isOutputFilesActive
                                    ? 'bg-blue-100 text-blue-800 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                {isOutputFilesActive && (
                                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />
                                )}
                                <span className="mr-1">📎</span> Output Files
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Submit Refinement Link */}
                {task.status !== TaskStatus.PROCESSING && (
                  <div className="mt-2 pt-3 border-t border-gray-200" data-nav-id="submit-refinement">
                    <button
                      onClick={() => navigateToElement('submit-refinement')}
                      className={`w-full text-left px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center ${
                        activeElementId === 'submit-refinement'
                          ? 'bg-blue-100 text-blue-800'
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {activeElementId === 'submit-refinement' && (
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />
                      )}
                      <span className="mr-1">✏️</span> Submit Refinement
                    </button>
                  </div>
                )}
              </nav>
            </div>
          </div>
        )}

        {/* Toggle sidebar button (when closed) */}
        {!sidebarOpen && navigationStructure.length > 0 && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="fixed left-4 top-4 z-40 p-2 bg-white border border-gray-200 rounded-lg shadow-md hover:bg-gray-50"
            title="Open navigation"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Main content */}
        <div
          className={`transition-all duration-300 py-8 ${
            sidebarOpen && navigationStructure.length > 0 ? 'ml-[280px]' : 'ml-0'
          }`}
        >
          <div className="max-w-4xl mx-auto px-4">
            <button
              onClick={() => navigate('/tasks')}
              className="text-sm text-gray-600 hover:text-black mb-4"
            >
              ← Back to Tasks
            </button>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-subtle mb-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-3xl font-bold">{task.title}</h1>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                {task.status.toUpperCase()}
              </span>
              {task.status === TaskStatus.DRAFT && (
                <>
                  <button
                    onClick={handleSubmitDraft}
                    disabled={isSubmittingDraft}
                    className="px-4 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingDraft ? 'Submitting...' : 'Submit Task'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md text-sm font-medium"
                  >
                    Delete Draft
                  </button>
                </>
              )}
              {(task.status === TaskStatus.COMPLETED || task.status === TaskStatus.FAILED) && (
                <>
                  <button
                    onClick={() => {
                      setKbTitle(`KB: ${task.title}`);
                      setKbDescription(task.description);
                      setShowConvertModal(true);
                    }}
                    className="px-3 py-1 btn-secondary rounded-md text-xs"
                  >
                    Convert to KB
                  </button>
                  <button
                    onClick={() => setShowArchiveConfirm(true)}
                    className="px-3 py-1 btn-secondary rounded-md text-xs"
                  >
                    Archive Task
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="text-gray-600 whitespace-pre-wrap">{task.description}</p>
          <div className="mt-4 text-xs text-gray-500">
            Created: {new Date(task.created_at).toLocaleString()}
          </div>

          {task.knowledge_bases && task.knowledge_bases.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium mb-2">Knowledge Bases</h3>
              <div className="space-y-2">
                {task.knowledge_bases.map((kb) => (
                  <div key={kb.id} className="flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 rounded group">
                    <a
                      href={`/knowledge-bases/${kb.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-sm text-blue-600 hover:underline flex-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <span className="font-medium">{kb.title}</span>
                      {kb.is_default && (
                        <span className="px-2 py-0.5 text-xs bg-blue-200 text-blue-800 rounded">Default</span>
                      )}
                      <span className="text-xs text-gray-600">
                        ({kb.resource_count} {kb.resource_count === 1 ? 'resource' : 'resources'})
                      </span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {task.files.filter(f => f.iteration_id === null).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium mb-2">Input Files</h3>
              <div className="space-y-2">
                {task.files.filter(f => f.iteration_id === null).map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group">
                    <a
                      href={`/tasks/${id}/files/${file.id}/preview`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-sm text-blue-600 hover:underline flex-1"
                    >
                      <span>📎</span>
                      <span>{file.file_name}</span>
                      <span className="text-gray-500">({formatFileSize(file.file_size)})</span>
                    </a>
                    <button
                      onClick={() => handleDownloadFile(file.id, file.file_name)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-white"
                    >
                      <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <h2 className="text-2xl font-bold mb-4">Iterations</h2>

        <div className="space-y-6">
          {task.iterations.map((iteration, iterIndex) => {
            // Create ID counter for handling duplicate headers
            const idCounts: Record<string, number> = {};
            const isExpanded = expandedIterations.has(iteration.id);
            const isAgentReportExpanded = expandedAgentReports.has(iteration.id);
            const isLatest = iterIndex === task.iterations.length - 1;
            // Get heading count from navigation structure
            const iterNav = navigationStructure.find(n => n.iterationId === iteration.id);
            const countHeadings = (nodes: NavigationNode[]): number => {
              return nodes.reduce((count, node) => count + 1 + countHeadings(node.children), 0);
            };
            const headingCount = iterNav ? countHeadings(iterNav.tree) : 0;

            // Helper to generate unique header ID (matches parseMarkdownHeaders logic)
            const getHeaderId = (text: string): string => {
              const baseId = String(text)
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');

              let id = baseId;
              if (idCounts[baseId] !== undefined) {
                idCounts[baseId]++;
                id = `${baseId}-${idCounts[baseId]}`;
              } else {
                idCounts[baseId] = 0;
              }
              return generateHeaderId(iteration.id, id);
            };

            // Create custom markdown components for this iteration
            const markdownComponents: Partial<Components> = {
              h1: ({ children, ...props }) => (
                <h1 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h1>
              ),
              h2: ({ children, ...props }) => (
                <h2 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h2>
              ),
              h3: ({ children, ...props }) => (
                <h3 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h3>
              ),
              h4: ({ children, ...props }) => (
                <h4 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h4>
              ),
              h5: ({ children, ...props }) => (
                <h5 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h5>
              ),
              h6: ({ children, ...props }) => (
                <h6 id={getHeaderId(String(children))} className="scroll-mt-4" {...props}>{children}</h6>
              ),
            };

            return (
              <div
                key={iteration.id}
                id={`iteration-${iteration.id}`}
                className="bg-white border border-gray-200 rounded-lg shadow-subtle scroll-mt-4 overflow-hidden"
              >
                {/* Collapsible Header */}
                <button
                  onClick={() => toggleIterationExpand(iteration.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-left">
                      Iteration {iteration.iteration_number}
                      {isLatest && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Latest</span>
                      )}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(iteration.status)}`}>
                      {iteration.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {iteration.completed_at
                        ? new Date(iteration.completed_at).toLocaleDateString()
                        : new Date(iteration.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </button>

                {/* Collapsible Content - only rendered when expanded */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    {iteration.user_prompt && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm font-medium text-gray-700 mb-1">User Prompt:</p>
                        <p className="text-sm text-gray-600">{iteration.user_prompt}</p>
                      </div>
                    )}

                    {iteration.agent_report && (
                      <div className="mt-4 bg-blue-50 rounded overflow-hidden">
                        {/* Collapsible Agent Report Header */}
                        <button
                          onClick={() => toggleAgentReportExpand(iteration.id)}
                          className="w-full flex items-center justify-between p-3 hover:bg-blue-100 transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <svg
                              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isAgentReportExpanded ? 'rotate-90' : ''}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">Agent Report</span>
                          </div>
                          {!isAgentReportExpanded && headingCount > 0 && (
                            <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">
                              {headingCount} section{headingCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </button>
                        {/* Collapsible Agent Report Content */}
                        {isAgentReportExpanded && (
                          <div className="px-3 pb-3 prose prose-sm max-w-none markdown-content">
                            <ReactMarkdown components={markdownComponents}>{iteration.agent_report}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}

                    {iteration.output_files.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <h4 id={`iteration-${iteration.id}-output-files`} className="text-sm font-medium scroll-mt-4">Output Files</h4>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadIterationOutputFiles(iteration.id, iteration.iteration_number);
                            }}
                            className="px-2 py-1 text-xs btn-primary rounded flex items-center gap-1"
                            title="Download all output files as ZIP"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download All
                          </button>
                        </div>
                        <div className="space-y-2">
                          {iteration.output_files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group">
                              <a
                                href={`/tasks/${id}/files/${file.id}/preview`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2 flex-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>{file.file_name}</span>
                                <span className="text-xs text-gray-500">({formatFileSize(file.file_size)})</span>
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFile(file.id, file.file_name);
                                }}
                                className="ml-2 p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Download"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 text-xs text-gray-500">
                      {iteration.completed_at ? (
                        <span>Completed: {new Date(iteration.completed_at).toLocaleString()}</span>
                      ) : (
                        <span>Started: {new Date(iteration.created_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {task.status !== TaskStatus.PROCESSING && (
          <div id="submit-refinement" className="mt-6 bg-white border border-gray-200 rounded-lg p-6 shadow-subtle scroll-mt-4">
            <h3 className="text-lg font-semibold mb-4">Add Refinement</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Refinement Request
              </label>
              <textarea
                value={newPrompt}
                onChange={(e) => setNewPrompt(e.target.value)}
                placeholder="Describe how you'd like the agent to refine this task..."
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach Files (Optional)
              </label>
              <FileUploader
                onFilesChange={setRefinementFiles}
                initialFiles={refinementFiles}
                maxFiles={10}
                maxSize={10 * 1024 * 1024}
              />
            </div>

            <button
              onClick={handleAddIteration}
              disabled={isSubmitting || !newPrompt.trim()}
              className="px-6 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Refinement'}
            </button>
          </div>
        )}

      {/* Convert to KB Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Convert to Knowledge Base</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={kbTitle}
                  onChange={(e) => setKbTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md"
                  placeholder="Knowledge base title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={kbDescription}
                  onChange={(e) => setKbDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md"
                  placeholder="Optional description"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeInputFiles}
                    onChange={(e) => setIncludeInputFiles(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Include input files</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeOutputFiles}
                    onChange={(e) => setIncludeOutputFiles(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Include output files</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={handleConvertToKB}
                disabled={isConverting || !kbTitle.trim()}
                className="px-6 py-2 btn-primary rounded-md text-sm disabled:opacity-50"
              >
                {isConverting ? 'Converting...' : 'Convert'}
              </button>
              <button
                onClick={() => setShowConvertModal(false)}
                className="px-6 py-2 btn-secondary rounded-md text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Draft Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Delete Draft Task?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this draft task? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDeleteDraft}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md text-sm disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-6 py-2 btn-secondary rounded-md text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Task Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Archive Task?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to archive this task? You can unarchive it later from the archived tasks view.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleArchiveTask}
                disabled={isArchiving}
                className="px-6 py-2 btn-primary rounded-md text-sm disabled:opacity-50"
              >
                {isArchiving ? 'Archiving...' : 'Archive'}
              </button>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                disabled={isArchiving}
                className="px-6 py-2 btn-secondary rounded-md text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </>
  );
}
